import { useEffect, useState } from "react";
import type { Agent } from "@baton/contracts";
import type { DashboardApi } from "./api.js";
import type { CryptoWorkerClient } from "./crypto-client.js";
import type { DecryptedProjectGraph } from "./projects-view.js";
import type { DecryptedMessage } from "./messages.js";
import type { SendDraft } from "./hooks/use-project-messages.js";
import { buildMessageContent, emptyHandoffDraft } from "./compose.js";
import { HandoffFields } from "./handoff-fields.js";
import { HandoverBlock } from "./handover-block.js";
import { Modal } from "./components/modal.js";
import { Command } from "./components/command.js";

export function generalRecipients(graph: DecryptedProjectGraph, userId: string) {
  const active = new Set(graph.members.filter((member) => member.state === "active" && member.userId !== userId).map((member) => member.userId));
  return [...new Map(graph.agentAssignments.filter(({ agent }) => agent.role === null && !agent.retiredAt && active.has(agent.ownerUserId)).map(({ agent }) => [agent.id, {
    id: agent.id,
    handle: graph.members.find((member) => member.userId === agent.ownerUserId)!.handle,
  }])).values()].sort((a, b) => a.handle.localeCompare(b.handle));
}

/** A person is the destination; their general agent receives the encrypted document. */
export function HandoverDialog(props: {
  graph: DecryptedProjectGraph;
  userId: string;
  onSend(draft: SendDraft): Promise<void>;
  onClose(): void;
}) {
  const recipients = generalRecipients(props.graph, props.userId);
  const [recipientId, setRecipientId] = useState(recipients[0]?.id ?? "");
  const [mode, setMode] = useState<"mcp" | "write">("write");
  const [client, setClient] = useState<"codex" | "claude">("codex");
  const [body, setBody] = useState("");
  const [draft, setDraft] = useState(() => emptyHandoffDraft(props.graph.repositories));
  const [working, setWorking] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recipient = recipients.find((candidate) => candidate.id === recipientId);
  async function send(event: React.FormEvent) {
    event.preventDefault();
    if (working || !recipient) return;
    setWorking(true); setError(null);
    try {
      const content = buildMessageContent({
        type: "handoff", body,
        handoff: { ...draft, subject: draft.subject.trim() || `Handover: ${props.graph.project.name}`, notes: draft.notes.trim() || (!draft.done.trim() && !draft.todo.trim() && !draft.verify.trim() && !draft.ideas.trim() && !draft.repositoryId ? body : "") },
        project: props.graph.project,
        repository: props.graph.repositories.find((repo) => repo.id === draft.repositoryId) ?? null,
      });
      await props.onSend({ type: "handoff", recipientAgentIds: [recipient.id], content, repoId: draft.repositoryId });
      setSentTo(recipient.handle);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not send the handover."); }
    finally { setWorking(false); }
  }
  return <Modal open onClose={props.onClose} title="Hand over to a collaborator">
    <div className="handover-dialog" data-testid="handover-dialog">
      {sentTo ? <div role="status">
        <h4>Handover sent to @{sentTo}</h4>
        <p>Their general agent has the handover. They will also receive a Telegram notification if they have linked the bot.</p>
        <button type="button" className="primary" onClick={props.onClose}>Done</button>
      </div> : <>
        <label>Collaborator<select disabled={working} value={recipientId} onChange={(event) => setRecipientId(event.target.value)}>
          {recipients.map((candidate) => <option key={candidate.id} value={candidate.id}>@{candidate.handle}</option>)}
          {!recipients.length && <option value="">No collaborators yet</option>}
        </select></label>
        {!recipients.length && <p className="muted">A project admin can invite a repository collaborator from Manage project. They appear here after signing in and accepting the invitation.</p>}
        <div className="tabs" role="tablist" aria-label="Handover method">
          <button type="button" role="tab" disabled={working} aria-selected={mode === "write"} onClick={() => setMode("write")}>Send from browser</button>
          <button type="button" role="tab" disabled={working} aria-selected={mode === "mcp"} onClick={() => setMode("mcp")}>Coding tool (MCP)</button>
        </div>
        {mode === "mcp" ? <>
          <p>Open your general agent in the repository you are working on, then ask it to prepare the handover from your session.</p>
          <label>Coding tool<select value={client} onChange={(event) => setClient(event.target.value as "codex" | "claude")}><option value="codex">Codex</option><option value="claude">Claude Code</option></select></label>
          <Command label="Open your general agent" value={`baton open general ${client}`} />
          {recipient && <Command label="Say this to your agent" kind="prompt" value={`Hand over project ${JSON.stringify(props.graph.project.name)} (project ID ${props.graph.project.id}) to @${recipient.handle}. Use baton_handover with general-agent routing. Write a handover document from this session: work done, remaining tasks, verification steps, repository and branch, and any uncommitted work. Prepare it for my review, then send after I approve.`} />}
          <p className="muted compact">The agent prepares the document; you review it before it is sent. Your collaborator receives it through their general agent.</p>
        </> : <form onSubmit={(event) => void send(event)}>
          <p>Write or paste your handover here. No Node.js, Baton installation, or running coding tool is needed to send.</p>
          <label>What should they pick up?<textarea required disabled={working} rows={5} value={body} onChange={(event) => setBody(event.target.value)} placeholder="Where you stopped, what is left, and how to check the work." /></label>
          <details><summary>Add handover details</summary><HandoffFields value={draft} onChange={setDraft} repositories={props.graph.repositories} showRepositoryPicker /></details>
          {error && <p role="alert" className="error">{error}</p>}
          <p className="muted compact">Sent from your general agent to @{recipient?.handle ?? "your collaborator"}'s general agent.</p>
          <button type="submit" className="primary" disabled={working || !recipient || !body.trim()}>{working ? "Sending…" : "Send handover"}</button>
        </form>}
      </>}
    </div>
  </Modal>;
}

export function HandoverReader(props: {
  messageId: string;
  api: DashboardApi;
  worker: CryptoWorkerClient;
  ownAgents: Agent[];
  onProject(projectId: string): void;
  onClose(): void;
}) {
  const [message, setMessage] = useState<DecryptedMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<"codex" | "claude">("codex");
  useEffect(() => {
    let cancelled = false;
    setMessage(null); setError(null);
    void props.api.message(props.messageId).then(async (raw) => {
      if (raw.type !== "handoff") throw new Error("This link does not point to a handover.");
      const [decrypted] = await props.worker.call<DecryptedMessage[]>("decryptMessages", { messages: [raw] });
      if (!decrypted) throw new Error("Could not read the handover.");
      if (cancelled) return;
      setMessage(decrypted);
      if (decrypted.projectId) props.onProject(decrypted.projectId);
    }).catch((caught: unknown) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "This handover is unavailable."); });
    return () => { cancelled = true; };
  }, [props.api, props.worker, props.messageId, props.onProject]);
  const isRecipient = message?.recipientAgentIds.some((id) => props.ownAgents.some((agent) => agent.id === id));
  return <Modal open onClose={props.onClose} title="Your handover">
    <div className="handover-dialog" data-testid="handover-reader">
      {error && <p role="alert" className="error">{error}</p>}
      {!message && !error && <p role="status">Loading handover…</p>}
      {message && <>
        <h4>{message.content.subject ?? "Handover"}</h4>
        <p className="chat-body">{message.content.body}</p>
        {message.content.handover && <HandoverBlock document={message.content.handover} />}
        {isRecipient && <>
          <label>Continue in<select value={client} onChange={(event) => setClient(event.target.value as "codex" | "claude")}><option value="codex">Codex</option><option value="claude">Claude Code</option></select></label>
          <Command label="Run in your repository" value={`baton handover ${message.id} ${client}`} />
          <p className="muted compact">Your agent opens this handover, acknowledges it when you ask it to take the work, and helps you continue.</p>
        </>}
      </>}
    </div>
  </Modal>;
}

export function ProjectMessagesDialog(props: {
  graph: DecryptedProjectGraph;
  userId: string;
  messages: DecryptedMessage[];
  error: string | null;
  onChat(agentId: string): void;
  onHandover(messageId: string): void;
  onClose(): void;
}) {
  const recipients = generalRecipients(props.graph, props.userId);
  const handovers = props.messages.filter((message) => message.type === "handoff" && message.projectId === props.graph.project.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return <Modal open onClose={props.onClose} title="Messages">
    <div className="handover-dialog" data-testid="project-messages">
      <h4>Message a collaborator</h4>
      <div className="collaborator-buttons">{recipients.map((recipient) => <button type="button" className="sidebar-button" key={recipient.id} onClick={() => props.onChat(recipient.id)}>@{recipient.handle}</button>)}</div>
      {!recipients.length && <p className="muted">Your collaborators appear here after joining the project.</p>}
      <h4>Handovers</h4>
      {props.error && <p role="alert" className="error">{props.error}</p>}
      {!handovers.length && !props.error && <p className="muted">No handovers yet.</p>}
      <ul className="invite-list">{handovers.map((message) => <li key={message.id}><span>{message.content.subject ?? "Handover"}</span><button type="button" onClick={() => props.onHandover(message.id)}>Open handover</button></li>)}</ul>
    </div>
  </Modal>;
}

export function readHandoverId(url: URL): string | null {
  const candidate = url.searchParams.get("handover") ?? /^\/handover\/([^/]+)$/.exec(url.pathname)?.[1];
  return candidate && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(candidate) ? candidate : null;
}
